import { PropsWithChildren, useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';

import { DateTime, PriceType, TimeRange } from '@cast/shared-types';

import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import {
  ComputeCostDynamicCopiesMap,
  CostOverTimeContext,
} from 'features/cost-report/providers/CostOverTimeProvider';
import {
  useClusterAnomaliesQuery,
  useCostReportQuery,
} from 'hooks/queries/cost';
import { ChartType } from 'types/charts';
import { DataGranularity } from 'types/cost-report';
import { compensateDSTv2 } from 'utils/compensateDST';
import { fillGaps } from 'utils/metrics';

import {
  aggregateClusterMetrics,
  emptyDataPoint,
  mapClusterCostData,
} from './_utils';

import { useCluster } from '@cast/shared-cluster-context-data-access';

// prettier-ignore
const clusterCopiesMap: ComputeCostDynamicCopiesMap = {
  summary: {
    totalSpendTooltip: 'Total cluster compute spend for the selected period',
    currentSpendTooltip: 'Current month factual compute spend that includes the end of the current day projection',
    monthlyForecastTooltip: 'Takes into account factual workload uptime and forecast for the rest of the month. Forecast is calculated using 7 day moving average formula.',
    monthlyAverageTooltip: 'Average monthly compute cost for selected time period. Calculated as daily average multiplied by 30 days',
    averageCpuCost: 'Average normalized cost per 1 provisioned CPU',
  },
  computeSpend: {
    mainTitle: 'Projected end of day spend',
    mainTooltip: 'Calculated by adding current day actual spend and calculated cost projection for the reminder of the day',
  },
  normalized: {
    mainDefaultTooltip: 'End of the day projected cost per normalized CPU',
    mainMonthlyTooltip: 'Monthly cost projection for 1 CPU, calculated as total hourly compute spend divided by the provisioned CPU and multiplied by 730',
    onDemandDefaultTooltip: 'End of the day projected cost per normalized on-demand CPU',
    onDemandMonthlyTooltip: 'Monthly cost projection for 1 on-demand CPU, calculated as total on-demand hourly compute spend divided by the provisioned on-demand CPU and multiplied by 730',
    fallbackDefaultTooltip: 'End of the day projected cost per normalized fallback CPU',
    fallbackMonthlyTooltip: 'Monthly cost projection for 1 fallback CPU, calculated as total fallback hourly compute spend divided by the provisioned fallback  CPU and multiplied by 730',
    spotDefaultTooltip: 'End of the day projected cost per normalized spot CPU',
    spotMonthlyTooltip: 'Monthly cost projection for 1 spot CPU, calculated as total spot hourly compute spend divided by the provisioned spot CPU and multiplied by 730',
  },
};

export const ClusterCostOverTimeProvider = ({
  children,
}: PropsWithChildren<unknown>) => {
  const clusterId = useCluster().cluster.id;
  const {
    isDefaultFilter,
    isCapabilitiesLoading,
    capabilitiesError,
    refetchCapabilities,
    timezone,
    chartType,
    granularity: _granularity,
    granularDateRange,
    useListingPrices,
  } = useCostReportContext();

  const granularity: DataGranularity = useMemo(
    () =>
      chartType === ChartType.BAR ? { step: 1, unit: 'day' } : _granularity,
    [chartType, _granularity]
  );

  const previousRange = useMemo<TimeRange<DateTime>>(
    () => ({
      from: granularDateRange.from.subtract(1, 'M'),
      to: granularDateRange.to.subtract(1, 'M'),
    }),
    [granularDateRange]
  );

  const {
    isLoading: isLoadingCurrentRange,
    costReport: currentRangeData,
    error: currentError,
    refetch: refetchCurrentRange,
  } = useCostReportQuery({
    id: clusterId,
    startTime: granularDateRange.from.toISOString(),
    endTime: granularDateRange.to.toISOString(),
    granularity,
    useListingPrices,
  });

  const {
    isLoading: isLoadingAnomalies,
    anomalies,
    error: anomaliesError,
    refetch: refetchAnomalies,
  } = useClusterAnomaliesQuery({
    clusterId,
    startTime: granularDateRange.from.toISOString(),
    endTime: granularDateRange.to.toISOString(),
  });

  const {
    isLoading: isLoadingPreviousRange,
    costReport: previousRangeData,
    error: previousError,
    refetch: refetchPreviousRange,
  } = useCostReportQuery({
    id: clusterId,
    startTime: previousRange.from.toISOString(),
    endTime: previousRange.to.toISOString(),
    granularity,
    enabled: isDefaultFilter,
    useListingPrices,
  });

  const currentData = useMemo(
    () =>
      mapClusterCostData({
        data: currentRangeData?.items ?? [],
        endTime: granularDateRange.to,
        granularity,
      }),
    [currentRangeData?.items, granularDateRange.to, granularity]
  );

  const currentChartData = useMemo(() => {
    return compensateDSTv2(
      fillGaps({
        timeSeries: currentData,
        dateRange: granularDateRange,
        filler: { ...emptyDataPoint, generated: true },
        unit: granularity.unit,
        step: granularity.step,
      }),
      timezone
    );
  }, [
    currentData,
    granularDateRange,
    granularity.unit,
    granularity.step,
    timezone,
  ]);

  const previousData = useMemo(
    () =>
      mapClusterCostData({
        data: previousRangeData?.items ?? [],
        endTime: previousRange.to,
        granularity,
      }),
    [previousRange, previousRangeData?.items, granularity]
  );

  const aggregatedMetrics = useMemo(
    () =>
      aggregateClusterMetrics({
        currentData,
        previousData,
        ...(!isEmpty(currentRangeData?.summary) && {
          currentSummary: currentRangeData?.summary,
        }),
        ...(!isEmpty(previousRangeData?.summary) && {
          previousSummary: previousRangeData?.summary,
        }),
        timezone,
        isDefaultFilter,
        granularity,
      }),
    [
      currentData,
      previousData,
      currentRangeData?.summary,
      previousRangeData?.summary,
      timezone,
      isDefaultFilter,
      granularity,
    ]
  );

  const isLoading =
    isLoadingCurrentRange ||
    isLoadingPreviousRange ||
    isLoadingAnomalies ||
    isCapabilitiesLoading;

  const isCollecting = isDefaultFilter
    ? isLoading ||
      (!currentRangeData?.items.length && !previousRangeData?.items.length)
    : isLoading;

  return (
    <CostOverTimeContext.Provider
      value={{
        key: 'cluster',
        isCollecting,
        hasData: Boolean(currentRangeData?.items.length),
        error:
          currentError || previousError || anomaliesError || capabilitiesError,
        refetch: () => {
          refetchCurrentRange();
          refetchPreviousRange();
          refetchAnomalies();
          refetchCapabilities();
        },
        projectEndOfDayCost: true,
        applyUptime: true,
        timeSeriesStep: PriceType.HOURLY,
        storageCostGranularity: PriceType.DAILY,
        current: {
          isLoading: isLoadingCurrentRange || isCapabilitiesLoading,
          data: currentData,
          chartData: currentChartData,
          error: currentError,
          summary: currentRangeData?.summary,
        },
        previous: {
          isLoading: isLoadingPreviousRange,
          data: previousData,
          chartData: [],
          error: previousError,
          summary: previousRangeData?.summary,
        },
        aggregatedMetrics,
        dynamicCopiesMap: clusterCopiesMap,
        anomalies,
      }}
    >
      {children}
    </CostOverTimeContext.Provider>
  );
};
